import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/constants/brand";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("iamc_token", data.access_token);
      toast.success(`Benvenuto, ${data.user.name}!`);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-obsidian text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div className="hero-glow w-[420px] h-[420px] bg-brand/20 -top-32 -right-32" />
      <div className="hero-glow w-[320px] h-[320px] bg-brand-deep/15 bottom-0 -left-32" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="bg-card2/80 backdrop-blur-md border border-brand/25 rounded-3xl p-8 sm:p-10">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-2xl object-cover ring-1 ring-brand/40 mb-5" />
            <h1 className="font-display text-2xl font-extrabold text-white" data-testid="admin-login-title">
              Area Riservata
            </h1>
            <p className="text-sm text-muted2 mt-1.5">Pannello di controllo — Io Amo La Mia Città</p>
          </div>

          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-white mb-2">Email</label>
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bullmarketing.it"
                className="w-full bg-surface border border-input focus:border-brand rounded-xl px-4 py-3.5 text-white placeholder:text-dim outline-none transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-white mb-2">Password</label>
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="w-full bg-surface border border-input focus:border-brand rounded-xl px-4 py-3.5 text-white placeholder:text-dim outline-none transition-colors duration-300"
              />
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-sm font-semibold text-brand-coral bg-brand-coral/10 border border-brand-coral/30 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hot disabled:opacity-60 text-white font-display font-bold text-base px-8 py-4 rounded-full transition-colors duration-300"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />}
              {loading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>
        </div>

        <p className="text-center mt-6">
          <Link to="/" data-testid="admin-back-home-link" className="text-sm text-dim hover:text-brand transition-colors duration-300">
            ← Torna alla home
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
